import { useState, useEffect } from 'react'
import { Loading } from '../../../components/loading/Loading';
import { useDispatch, useSelector } from 'react-redux';
import { InputText } from '../../../components/inputs/InputText';
import { InputDateWithIcon } from '../../../components/inputDateWithIcon/InputDateWithIcon';
import { InputPhone } from '../../../components/inputPhone/InputPhone';
import { InputCurrency } from '../../../components/inputs/InputCurrency';
import { ComboBox } from '../../../components/comboBox/ComboBox';
import { locationState, levelEducation, statusStudents } from '../../../static/data';
import { toast } from 'react-hot-toast';
import { updateProfileUser, getUserById } from '../../../redux/actions/users';
import { Button } from '../../../components/buttons/Button';

// DataField component moved outside to prevent re-creation on each render
const DataField = ({ label, value, name, type = "text", isEditMode, handleInputChange, editable = true, currency = false, formData, setFormData, comboBoxData, query, setQuery, capitalize = true, beforeToDay = false }) => {
    if (isEditMode && editable) {
        // Use InputCurrency for currency fields
        if (currency && type === "number") {
            return (
                <InputCurrency
                    name={name}
                    label={label}
                    formData={formData}
                    setFormData={setFormData}
                    placeholder={label}
                    disabled={false}
                />
            );
        }

        // Use ComboBox for combobox type
        if (type === "combobox" && comboBoxData) {
            return (
                <div className="space-y-1.5">
                    <label className="text-sm font-semibold text-gray-700">{label}</label>
                    <ComboBox
                        filterData={comboBoxData}
                        query={query}
                        setQuery={setQuery}
                        selected={formData}
                        setSelected={setFormData}
                        placeholder={label}
                        property={name}
                    />
                </div>
            );
        }

        // Use InputText component for text and email types
        if (type === "text" || type === "email") {
            return (
                <InputText
                    id={name}
                    name={name}
                    type={type}
                    label={label}
                    onChange={handleInputChange}
                    value={value}
                    placeholder={label}
                    disabled={false}
                />
            );
        }
        // Use InputPhone for tel type
        if (type === "tel") {
            return (
                <InputPhone
                    id={name}
                    name={name}
                    label={label}
                    onChange={handleInputChange}
                    value={value}
                    placeholder={label}
                    disabled={false}
                />
            );
        }


        // Use InputDateWithIcon for date type
        if (type === "date") {
            // Convert string date to Date object, or use today's date if empty
            const dateValue = value ? new Date(value) : new Date();

            // Create a specific handler for this date field
            const handleSpecificDateChange = (date) => {
                setFormData(prev => ({
                    ...prev,
                    [name]: date ? date.toISOString() : ''
                }));
            };

            return (
                <InputDateWithIcon
                    id={name}
                    label={label}
                    onChange={handleSpecificDateChange}
                    value={dateValue}
                    placeHolder={label}
                    beforeToDay={beforeToDay}
                />
            );
        }
        // Fallback to regular input for other types
        return (
            <div className="space-y-1.5">
                <label className="text-sm font-semibold text-gray-700">{label}</label>
                <input
                    type={type}
                    name={name}
                    value={value}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors text-sm"
                />
            </div>
        );
    }

    // View mode - format based on type
    let displayValue = value;

    // Format combobox - extract description from object
    if (type === "combobox" && value && typeof value === 'object') {
        displayValue = value.description || value;
    }

    // Format date
    if (type === "date" && value) {
        displayValue = new Date(value).toLocaleDateString('es-MX', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    }

    // Format currency (Mexican Pesos)
    if (currency && value) {
        displayValue = new Intl.NumberFormat('es-MX', {
            style: 'currency',
            currency: 'MXN'
        }).format(value);
    }

    // Add visual indicator for non-editable fields in edit mode
    const containerClass = isEditMode && !editable
        ? "space-y-1 bg-gray-50 p-3 rounded-lg border border-gray-200"
        : "space-y-1";

    return (
        <div className={containerClass}>
            <label className="text-[.7rem] font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1">
                {label}
                {isEditMode && !editable && (
                    <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                )}
            </label>
            <p className={`text-[.9rem] font-medium text-gray-900 break-words ${capitalize ? 'capitalize' : ''}`}>
                {displayValue || '-'}
            </p>
        </div>
    );
};

export const TabUserData = ({ isOpen, onEditModeChange, onSaveCallbackChange, onCancelCallbackChange, onLoadingSavingChange, onUserUpdated, userId, refreshTable }) => {
    const dispatch = useDispatch();
    const { userSelected, loadingUserSelected } = useSelector((state) => state.users);
    const { user } = useSelector((state) => state.user);
    const { typeUser: typeUserLoggin, _id: userLoggedId } = user;
    const [isEditMode, setIsEditMode] = useState(false);
    const [formData, setFormData] = useState({});
    const [selectedDate, setSelectedDate] = useState(null);
    const [findLocation, setFindLocation] = useState('');
    const [findEducation, setFindEducation] = useState('');
    const [findStatusStudent, setFindStatusStudent] = useState('');
    const canEditStatusUser = ["director", "administrativo", "control escolar", "cobranza", "ventas"];
    const canEditSubscriptionPayment = ["director", "administrativo", "control escolar", "cobranza", "ventas"];
    const canEditAmountAndTypePayment = ["director", "administrativo", "control escolar", "cobranza", "ventas"];

    // Initialize form data when userSelected changes
    useEffect(() => {
        if (userSelected) {
            setFormData({
                firstName: userSelected.firstName || '',
                secondName: userSelected.secondName || '',
                lastName: userSelected.lastName || '',
                secondSurname: userSelected.secondSurname || '',
                email: userSelected.email || '',
                phone: userSelected.phone || '',
                dateBirth: userSelected.dateBirth || '',
                location: userSelected.location ? { value: userSelected.location, description: userSelected.location } : '',
                zipCode: userSelected.zipCode || '',
                education: userSelected.education ? { value: userSelected.education, description: userSelected.education } : '',
                matricula: userSelected.matricula || '',
                typeUser: userSelected.typeUser || '',
                emailTax: userSelected.emailTax || '',
                rfc: userSelected.rfc || '',
                businessName: userSelected.businessName || '',
                taxRegime: userSelected.taxRegime || '',
                subscriptionPayment: userSelected.subscriptionPayment || '',
                paymentDeadlineDate: userSelected.paymentDeadlineDate || '',
                status: userSelected.status ? statusStudents.find(item => item.value === userSelected.status) || { value: userSelected.status, description: userSelected.status } : '',
                usageRights: userSelected.usageRights || '',
                lastDatePayment: userSelected.lastDatePayment || '',
                statusSubscription: userSelected.statusSubscription || '',
                lastPaymentAmount: userSelected.lastPaymentAmount || '',
                lastPaymentType: userSelected.lastPaymentType || '',
                paidWeeks: userSelected.paidWeeks || ''
            });

            // Convert dateBirth string to Date object for DatePicker
            if (userSelected.dateBirth) {
                setSelectedDate(new Date(userSelected.dateBirth));
            } else {
                setSelectedDate(null);
            }
        }
    }, [userSelected]);

    // Reset edit mode when drawer closes
    useEffect(() => {
        if (!isOpen && isEditMode) {
            handleCancel();
        }
    }, [isOpen]);

    // Notify parent when edit mode changes
    useEffect(() => {
        if (onEditModeChange) {
            onEditModeChange(isEditMode);
        }
    }, [isEditMode, onEditModeChange]);

    // Register callbacks with parent - update whenever formData changes
    useEffect(() => {
        if (onSaveCallbackChange) {
            onSaveCallbackChange(() => handleSave);
        }
        if (onCancelCallbackChange) {
            onCancelCallbackChange(() => handleCancel);
        }
    }, [onSaveCallbackChange, onCancelCallbackChange, formData, selectedDate, userSelected]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleDateChange = (date) => {
        setSelectedDate(date);
        setFormData(prev => ({
            ...prev,
            dateBirth: date ? date.toISOString() : ''
        }));
    };

    const handleCancel = () => {
        // Reset to original values
        setFormData({
            firstName: userSelected.firstName || '',
            secondName: userSelected.secondName || '',
            lastName: userSelected.lastName || '',
            secondSurname: userSelected.secondSurname || '',
            email: userSelected.email || '',
            phone: userSelected.phone || '',
            dateBirth: userSelected.dateBirth || '',
            location: userSelected.location ? { value: userSelected.location, description: userSelected.location } : '',
            zipCode: userSelected.zipCode || '',
            education: userSelected.education ? { value: userSelected.education, description: userSelected.education } : '',
            matricula: userSelected.matricula || '',
            typeUser: userSelected.typeUser || '',
            emailTax: userSelected.emailTax || '',
            rfc: userSelected.rfc || '',
            businessName: userSelected.businessName || '',
            taxRegime: userSelected.taxRegime || '',
            subscriptionPayment: userSelected.subscriptionPayment || '',
            paymentDeadlineDate: userSelected.paymentDeadlineDate || '',
            status: userSelected.status ? statusStudents.find(item => item.value === userSelected.status) || { value: userSelected.status, description: userSelected.status } : '',
            usageRights: userSelected.usageRights || '',
            lastDatePayment: userSelected.lastDatePayment || '',
            statusSubscription: userSelected.statusSubscription || '',
            lastPaymentAmount: userSelected.lastPaymentAmount || '',
            lastPaymentType: userSelected.lastPaymentType || '',
            paidWeeks: userSelected.paidWeeks || ''
        });

        // Reset date
        if (userSelected.dateBirth) {
            setSelectedDate(new Date(userSelected.dateBirth));
        } else {
            setSelectedDate(null);
        }

        setIsEditMode(false);
    };

    const validateForm = () => {
        const errors = [];

        // Validar firstName
        if (!formData.firstName || formData.firstName.trim() === '') {
            errors.push('El primer nombre es obligatorio');
        }

        // Validar lastName
        if (!formData.lastName || formData.lastName.trim() === '') {
            errors.push('El apellido paterno es obligatorio');
        }

        // Validar status - puede ser un objeto o un string
        const statusValue = formData.status?.value || formData.status;
        if (!statusValue || statusValue === '') {
            errors.push('El estado del usuario es obligatorio');
        }

        // Validar emailTax solo si tiene valor (es opcional, pero si existe debe ser válido)
        if (formData.emailTax && formData.emailTax.trim() !== '') {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(formData.emailTax)) {
                errors.push('El correo fiscal no es válido');
            }
        }

        // Validar subscriptionPayment solo si es estudiante
        if (formData.typeUser === 'estudiante') {
            const payment = parseFloat(formData.subscriptionPayment);
            if (isNaN(payment) || payment <= 0) {
                errors.push('El pago de suscripción debe ser mayor a 0');
            }
        }

        return errors;
    };

    const handleSave = () => {
        // Validar formulario
        const errors = validateForm();

        if (errors.length > 0) {
            // Mostrar todos los errores
            errors.forEach(error => {
                toast.error(error, {
                    duration: 4000,
                    position: 'top-right',
                });
            });
            return; // No continuar con el guardado
        }

        // Si no hay errores, proceder con el guardado
        // TODO: Dispatch action to save changes
        // console.log('Saving data:', formData);
        const updateState = {
            ...formData,
            dateBirth: formData.dateBirth ? formData.dateBirth : '',
            location: formData.location.description,
            zipCode: formData.zipCode,
            education: formData.education.description,
            firstName: formData.firstName ? formData.firstName.toLowerCase() : '',
            secondName: formData.secondName ? formData.secondName.toLowerCase() : '',
            lastName: formData.lastName ? formData.lastName.toLowerCase() : '',
            secondSurname: formData.secondSurname ? formData.secondSurname.toLowerCase() : '',
            rfc: formData.rfc ? formData.rfc.toLowerCase() : '',
            businessName: formData.businessName ? formData.businessName.toLowerCase() : '',
            subscriptionPayment: formData.subscriptionPayment ? parseFloat(formData.subscriptionPayment) : 0,
            paymentDeadlineDate: formData.paymentDeadlineDate ? formData.paymentDeadlineDate : '',
            status: formData.status.description,
        };

        //No se puede actulizar el usageRights desde el perfil del usuario
        delete updateState.usageRights;
        
        // Agregar el ID del usuario logueado como updatedBy para el registro de actividades
        updateState.updatedBy = userLoggedId;
        
        // Activar loading en el botón
        if (onLoadingSavingChange) {
            onLoadingSavingChange(true);
        }
        
        dispatch(updateProfileUser(updateState, userId))
            .then((response) => {
                if (response.status === 201) {
                    toast.success(response.message);
                    setIsEditMode(false);
                    dispatch(getUserById(userId));
                    // Refrescar la tabla de usuarios si el callback existe y no se esta refrescando la tabla
                    if (onUserUpdated && refreshTable) {
                        onUserUpdated();
                    }
                } else {
                    toast.error(response.message);
                }
            })
            .catch((error) => {
                toast.error(error.message || 'Error al guardar los cambios');
            })
            .finally(() => {
                // Desactivar loading en el botón
                if (onLoadingSavingChange) {
                    onLoadingSavingChange(false);
                }
            });
    };

    // Filter locations based on search query
    const filteredLocations = findLocation === ''
        ? locationState
        : locationState.filter((location) =>
            location.description
                .toLowerCase()
                .replace(/\s+/g, '')
                .includes(findLocation.toLowerCase().replace(/\s+/g, ''))
        );

    // Filter education levels based on search query
    const filteredEducations = findEducation === ''
        ? levelEducation
        : levelEducation.filter((grade) =>
            grade.description
                .toLowerCase()
                .replace(/\s+/g, '')
                .includes(findEducation.toLowerCase().replace(/\s+/g, ''))
        );

    // Filter student status based on search query
    const filteredStatusStudents = findStatusStudent === ''
        ? statusStudents
        : statusStudents.filter((statusStudent) =>
            statusStudent.description
                .toLowerCase()
                .replace(/\s+/g, '')
                .includes(findStatusStudent.toLowerCase().replace(/\s+/g, ''))
        );

    return (
        loadingUserSelected ? (
            <Loading />
        ) : (
            <div className="h-full flex flex-col">
                {/* Header fijo */}
                <div className="flex-shrink-0 mb-6">
                    <div className="bg-white border border-gray-200 rounded-lg px-5 py-3 lg:py-4 shadow-sm">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div>
                                    <h3 className="text-xl font-bold text-gray-900">
                                        Datos del Usuario
                                    </h3>
                                    <p className="text-xs text-gray-500 font-medium mt-0.5 hidden md:block">
                                        Información personal y de contacto
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                {!isEditMode && (
                                    <Button
                                        type="button"
                                        variant="drawerActions"
                                        onClick={() => setIsEditMode(true)}
                                        icon={
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                            </svg>
                                        }
                                    >
                                        Editar
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Contenido con scroll */}
                <div className="flex-1 overflow-auto pr-2">
                    <div className="space-y-6">
                        {/* Información Personal */}
                        <div className="bg-white border border-gray-200 rounded-lg p-5">
                            <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
                                <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                                <h4 className="text-lg font-bold text-gray-800">Información Personal</h4>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <DataField label="Primer Nombre" value={formData.firstName} name="firstName" isEditMode={isEditMode} handleInputChange={handleInputChange} handleDateChange={handleDateChange} selectedDate={selectedDate} formData={formData} setFormData={setFormData} />
                                <DataField label="Segundo Nombre" value={formData.secondName} name="secondName" isEditMode={isEditMode} handleInputChange={handleInputChange} handleDateChange={handleDateChange} selectedDate={selectedDate} formData={formData} setFormData={setFormData} />
                                <DataField label="Apellido Paterno" value={formData.lastName} name="lastName" isEditMode={isEditMode} handleInputChange={handleInputChange} handleDateChange={handleDateChange} selectedDate={selectedDate} formData={formData} setFormData={setFormData} />
                                <DataField label="Apellido Materno" value={formData.secondSurname} name="secondSurname" isEditMode={isEditMode} handleInputChange={handleInputChange} handleDateChange={handleDateChange} selectedDate={selectedDate} formData={formData} setFormData={setFormData} />
                                <DataField label="Fecha de Nacimiento" value={formData.dateBirth} name="dateBirth" type="date" isEditMode={isEditMode} handleInputChange={handleInputChange} handleDateChange={handleDateChange} selectedDate={selectedDate} formData={formData} setFormData={setFormData} />
                                <DataField label="Nivel Educativo" value={formData.education} name="education" type="combobox" isEditMode={isEditMode} handleInputChange={handleInputChange} handleDateChange={handleDateChange} selectedDate={selectedDate} formData={formData} setFormData={setFormData} comboBoxData={filteredEducations} query={findEducation} setQuery={setFindEducation} />
                            </div>
                        </div>

                        {/* Datos de Contacto */}
                        <div className="bg-white border border-gray-200 rounded-lg p-5">
                            <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
                                <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                                <h4 className="text-lg font-bold text-gray-800">Datos de Contacto</h4>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <DataField label="Correo Electrónico" value={formData.email} name="email" type="email" isEditMode={isEditMode} handleInputChange={handleInputChange} handleDateChange={handleDateChange} selectedDate={selectedDate} formData={formData} setFormData={setFormData} editable={false} capitalize={false} />
                                <DataField label="Teléfono" value={formData.phone} name="phone" type="tel" isEditMode={isEditMode} handleInputChange={handleInputChange} handleDateChange={handleDateChange} selectedDate={selectedDate} formData={formData} setFormData={setFormData} />
                                <DataField label="Ubicación" value={formData.location} name="location" type="combobox" isEditMode={isEditMode} handleInputChange={handleInputChange} handleDateChange={handleDateChange} selectedDate={selectedDate} formData={formData} setFormData={setFormData} comboBoxData={filteredLocations} query={findLocation} setQuery={setFindLocation} />
                                <DataField label="Código Postal" value={formData.zipCode} name="zipCode" isEditMode={isEditMode} handleInputChange={handleInputChange} handleDateChange={handleDateChange} selectedDate={selectedDate} formData={formData} setFormData={setFormData} />
                            </div>
                        </div>

                        {/* Información Institucional */}
                        <div className="bg-white border border-gray-200 rounded-lg p-5">
                            <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
                                <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                                <h4 className="text-lg font-bold text-gray-800">Información Institucional</h4>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <DataField label="Matrícula" value={formData.matricula} name="matricula" isEditMode={isEditMode} handleInputChange={handleInputChange} handleDateChange={handleDateChange} selectedDate={selectedDate} editable={false} formData={formData} setFormData={setFormData} capitalize={false} />
                                <DataField label="Tipo de Usuario" value={formData.typeUser} name="typeUser" isEditMode={isEditMode} handleInputChange={handleInputChange} handleDateChange={handleDateChange} selectedDate={selectedDate} editable={false} formData={formData} setFormData={setFormData} />

                                {/* Campo de Estado con colores */}
                                {isEditMode ? (
                                    <DataField label="Estado" value={formData.status} name="status" type="combobox" isEditMode={isEditMode} handleInputChange={handleInputChange} handleDateChange={handleDateChange} selectedDate={selectedDate} formData={formData} setFormData={setFormData} comboBoxData={filteredStatusStudents} query={findStatusStudent} setQuery={setFindStatusStudent} editable={canEditStatusUser.includes(typeUserLoggin)} />
                                ) : (
                                    <div className="space-y-1">
                                        <label className="text-[.7rem] font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1">
                                            Estado
                                        </label>
                                        <div className="flex items-center gap-2">
                                            {(formData.status?.value === 'activo' || formData.status === 'activo') && (
                                                <span className="inline-flex items-center gap-1.5 bg-emerald-100 text-emerald-700 px-3 py-1.5 rounded-lg font-semibold text-sm">
                                                    <span className="relative flex h-2 w-2">
                                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75"></span>
                                                        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                                                    </span>
                                                    Activo
                                                </span>
                                            )}
                                            {(formData.status?.value === 'inactivo' || formData.status === 'inactivo') && (
                                                <span className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-700 px-3 py-1.5 rounded-lg font-semibold text-sm">
                                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-gray-500"></span>
                                                    Inactivo
                                                </span>
                                            )}
                                            {(formData.status?.value === 'suspendido' || formData.status === 'suspendido') && (
                                                <span className="inline-flex items-center gap-1.5 bg-orange-100 text-orange-700 px-3 py-1.5 rounded-lg font-semibold text-sm">
                                                    <span className="relative flex h-2 w-2">
                                                        <span className="animate-pulse absolute inline-flex h-full w-full rounded-full bg-orange-500 opacity-75"></span>
                                                        <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-500"></span>
                                                    </span>
                                                    Suspendido
                                                </span>
                                            )}
                                            {(formData.status?.value === 'baja' || formData.status === 'baja') && (
                                                <span className="inline-flex items-center gap-1.5 bg-rose-100 text-rose-700 px-3 py-1.5 rounded-lg font-semibold text-sm">
                                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-rose-500"></span>
                                                    Baja
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                )}
                                {formData.typeUser === 'estudiante' && (
                                    <div className={`space-y-1 ${isEditMode ? 'bg-gray-50 p-3 rounded-lg border border-gray-200' : ''}`}>
                                        <label className="text-[.7rem] font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1">
                                            Derechos de Uso
                                            {isEditMode && (
                                                <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                                </svg>
                                            )}
                                        </label>
                                        <div className="flex items-center gap-2">
                                            {formData.usageRights ? (
                                                <>
                                                    <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                    <span className="text-[.9rem] font-semibold text-emerald-700">Firmado</span>
                                                </>
                                            ) : (
                                                <>
                                                    <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                    <span className="text-[.9rem] font-semibold text-red-700">No Firmado</span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Información de Suscripción (solo para estudiantes) */}
                        {formData.typeUser === 'estudiante' && (
                            <div className="bg-white border border-gray-200 rounded-lg p-5">
                                <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
                                    <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <h4 className="text-lg font-bold text-gray-800">Información de Suscripción</h4>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                                    {/* Campo de Estado de Suscripción con colores */}
                                    <div className={`space-y-1 ${isEditMode ? 'bg-gray-50 p-3 rounded-lg border border-gray-200' : ''}`}>
                                        <label className="text-[.7rem] font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1">
                                            Estado de Suscripción
                                            {isEditMode && (
                                                <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                                </svg>
                                            )}
                                        </label>
                                        <div className="flex items-center gap-2">
                                            {formData.statusSubscription === 'pagado' && (
                                                <span className="inline-flex items-center gap-1.5 bg-emerald-100 text-emerald-700 px-3 py-1.5 rounded-lg font-semibold text-sm">
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                    Pagado
                                                </span>
                                            )}
                                            {formData.statusSubscription === 'vencida' && (
                                                <span className="inline-flex items-center gap-1.5 bg-rose-100 text-rose-700 px-3 py-1.5 rounded-lg font-semibold text-sm">
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                    </svg>
                                                    Vencido
                                                </span>
                                            )}
                                            {!formData.statusSubscription && (
                                                <span className="text-[.9rem] font-medium text-gray-900">-</span>
                                            )}
                                        </div>
                                    </div>
                                    <DataField label="Tipo del Último Pago" value={formData.lastPaymentType} name="lastPaymentType" isEditMode={isEditMode} handleInputChange={handleInputChange} handleDateChange={handleDateChange} selectedDate={selectedDate} editable={false} formData={formData} setFormData={setFormData} />
                                    <DataField label="Última Fecha de Pago" value={formData.lastDatePayment} name="lastDatePayment" type="date" isEditMode={isEditMode} handleInputChange={handleInputChange} handleDateChange={handleDateChange} selectedDate={selectedDate} editable={false} formData={formData} setFormData={setFormData} />
                                    <DataField label="Monto del Último Pago" value={formData.lastPaymentAmount} name="lastPaymentAmount" type="number" isEditMode={isEditMode} handleInputChange={handleInputChange} handleDateChange={handleDateChange} selectedDate={selectedDate} editable={false} currency={true} formData={formData} setFormData={setFormData} />
                                    <DataField label="Fecha Límite de Pago" value={formData.paymentDeadlineDate} name="paymentDeadlineDate" type="date" isEditMode={isEditMode} handleInputChange={handleInputChange} handleDateChange={handleDateChange} selectedDate={selectedDate} editable={canEditSubscriptionPayment.includes(typeUserLoggin)} formData={formData} setFormData={setFormData} beforeToDay={true} />
                                    <DataField label="Pago de Suscripción" value={formData.subscriptionPayment} name="subscriptionPayment" type="number" isEditMode={isEditMode} handleInputChange={handleInputChange} handleDateChange={handleDateChange} selectedDate={selectedDate} editable={canEditAmountAndTypePayment.includes(typeUserLoggin)} currency={true} formData={formData} setFormData={setFormData} />
                                    <DataField label="Semanas Pagadas" value={formData.paidWeeks} name="paidWeeks" type="number" isEditMode={isEditMode} handleInputChange={handleInputChange} handleDateChange={handleDateChange} selectedDate={selectedDate} editable={false} formData={formData} setFormData={setFormData} />
                                </div>
                            </div>
                        )}

                        {/* Datos Fiscales (opcional) */}
                        {formData.typeUser === 'estudiante' && (
                            <div className="bg-white border border-gray-200 rounded-lg p-5">
                                <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
                                    <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    <h4 className="text-lg font-bold text-gray-800">Datos Fiscales</h4>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <DataField label="Correo Fiscal" value={formData.emailTax} name="emailTax" type="email" isEditMode={isEditMode} handleInputChange={handleInputChange} handleDateChange={handleDateChange} selectedDate={selectedDate} formData={formData} setFormData={setFormData} capitalize={false} />
                                    <DataField label="RFC" value={formData.rfc} name="rfc" isEditMode={isEditMode} handleInputChange={handleInputChange} handleDateChange={handleDateChange} selectedDate={selectedDate} formData={formData} setFormData={setFormData} capitalize={false} />
                                    <DataField label="Razón Social" value={formData.businessName} name="businessName" isEditMode={isEditMode} handleInputChange={handleInputChange} handleDateChange={handleDateChange} selectedDate={selectedDate} formData={formData} setFormData={setFormData} />
                                    <DataField label="Régimen Fiscal" value={formData.taxRegime} name="taxRegime" isEditMode={isEditMode} handleInputChange={handleInputChange} handleDateChange={handleDateChange} selectedDate={selectedDate} formData={formData} setFormData={setFormData} />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        )
    )
}